import { ReactNode, forwardRef } from 'react';

import { InfiniteScroll } from '../../InfiniteScroll';
import { CircularProgress } from '../../CircularProgress';
import { Box } from '../../Box';

import useStyles from './ListBox.styles';

/**
 * Свойства компонента {@link ListBox}
 */
interface ListBoxProps {
  /**
   * Указывает, есть ли еще элементы для загрузки.
   */
  hasMore: boolean;

  /**
   * Текущее количество загруженных элементов.
   */
  dataLength: number;

  /**
   * Функция обратного вызова для загрузки следующего набора элементов.
   */
  next: () => void;

  /**
   * Указывает, загружает ли компонент в настоящее время дополнительные элементы.
   */
  loading: boolean;

  /**
   * Дочерние элементы, которые будут отображаться в списке.
   */
  children?: ReactNode | ReactNode[];
}

/**
 * Компонент является контейнером для отображения списка элементов с бесконечной прокруткой.
 * Он оборачивает список в компонент `InfiniteScroll` и отображает индикатор загрузки,
 * когда загружаются дополнительные элементы.
 *
 * @internal
 * @param hasMore - Указывает, есть ли еще элементы для загрузки.
 * @param dataLength - Текущее количество загруженных элементов.
 * @param next - Функция обратного вызова для загрузки следующего набора элементов.
 * @param loading - Указывает, загружает ли компонент в настоящее время дополнительные элементы.
 * @param children - Дочерние элементы, которые будут отображаться в списке.
 * @param ref - Ссылка на базовый элемент div.
 * @returns JSX компонента.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link ListBoxProps}
 */
export const ListBox = forwardRef<HTMLDivElement, ListBoxProps>(
  ({ hasMore, dataLength, next, loading, children, ...rest }, ref) => {
    const { classes } = useStyles();

    return (
      <div
        {...rest}
        id="tree-view-autocomplete-scrollable"
        ref={ref}
        className={classes.scrollableWrapper}
      >
        <InfiniteScroll
          next={next}
          hasMore={hasMore}
          dataLength={dataLength}
          loader={
            loading && (
              <Box display="flex" justifyContent="center" p="16">
                <CircularProgress size={30} />
              </Box>
            )
          }
          endMessage={<div />}
          scrollId="tree-view-autocomplete-scrollable"
        >
          <ul className={classes.listBox}>{children}</ul>
        </InfiniteScroll>
      </div>
    );
  },
);
